import { create } from 'zustand';
import { getErrorMessage } from '../utils/messages';
import { IAuthStore, ICreateAccount, I2FASetupData } from '../interfaces/auth.interface';
import { IUser } from '../interfaces/user.interface';
import {
    removeAllCookies,
    setAuthCookie,
    setAuthCookies,
} from '../cookies/auth.cookies';
import {
    login,
    signup,
    loginWithToken,
    validateEmail,
    deliveryMarketingData,
    verify2FA,
    setup2FA,
    verifySetup2FA,
} from '../api/auth.api';
import { ESession } from '../enums/auth.enum';
import { clearDatadogIdentity } from '../plugins/datadog';
import { disconnectSocket } from '../sockets/socket.config';

const ERROR_MAP: Record<string, string> = {
    INVALID_CODE: 'El código ingresado no es válido. Verifica e intenta de nuevo.',
    CODE_ALREADY_USED: 'Este código ya fue utilizado. Espera a que se genere uno nuevo.',
    TEMP_TOKEN_EXPIRED: 'Tu sesión temporal expiró. Vuelve a iniciar sesión.',
    IP_MISMATCH: 'Se detectó un cambio de red. Vuelve a iniciar sesión.',
    TOKEN_BLOCKED: 'Demasiados intentos fallidos. Intenta de nuevo en 15 minutos.',
    ACCOUNT_LOCKED: 'Tu cuenta fue bloqueada temporalmente. Intenta de nuevo en 30 minutos.',
    ACTIVATION_FAILED: 'No se pudo activar la verificación en dos pasos. Intenta de nuevo.',
    INVALID_BACKUP_CODE: 'El código de respaldo no es válido o ya fue utilizado.',
    '2FA_NOT_ENABLED': 'La verificación en dos pasos no está activa en esta cuenta.',
};

const translate2FAError = (message: string): string => ERROR_MAP[message] || message;

export const useAuthStore = create<IAuthStore>((set) => ({
    token: null,
    emailExist: null,
    mustAnswerQuestion: false,
    data: null,
    loading: false,
    error: null,
    requires2FA: false,
    requires2FASetup: false,
    tempToken: null,
    graceInfo: null,
    setupData: null,
    setLoading: (loading: boolean) => set({ loading }),
    setError: (error: string | null) => set({ error }),
    setData: (data: IUser | null) => set({ data }),
    setEmailExist: (emailExist: boolean) => set({ emailExist }),
    setMustAnswerQuestion: (mustAnswerQuestion: boolean) => set({ mustAnswerQuestion }),
}));

export const startValidateEmail = async (email: string) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        const { data }: any = await validateEmail(email);
        useAuthStore.setState(() => ({
            emailExist: data as boolean,
            loading: false,
        }));
    } catch (error: any) {
        console.error('[startValidateEmail] Error', error);
        useAuthStore.setState(() => ({
            emailExist: false,
            loading: false,
        }));
    }
};

export const startLogin = async (email: string, password: string) => {
    try {
        useAuthStore.setState(() => ({ loading: true, error: null }));
        const { data }: any = await login({ email, password });

        if (data.requires2FA) {
            useAuthStore.setState(() => ({
                requires2FA: true,
                tempToken: data.tempToken,
                loading: false,
            }));
            return;
        }

        if (data.requires2FASetup) {
            useAuthStore.setState(() => ({
                requires2FASetup: true,
                tempToken: data.tempToken,
                graceInfo: data.graceInfo || null,
                loading: false,
            }));
            return;
        }

        setAuthCookies({
            token: data.token,
            userID: data._id,
        });

        if (data.graceInfo) {
            useAuthStore.setState(() => ({ graceInfo: data.graceInfo }));
        }

        useAuthStore.setState(() => ({
            data,
            loading: false,
        }));
    } catch (error: any) {
        console.error('[startLogin] Error', error);
        const errorMessage = getErrorMessage(error.message);
        useAuthStore.setState(() => ({ loading: false, error: errorMessage }));
    }
};

export const startLoadUser = async (_token: string) => {
    useAuthStore.setState(() => ({ loading: true }));
    const { data }: any = await loginWithToken();
    useAuthStore.setState(() => ({
        data,
        loading: false,
    }));
};

export const startLogout = async () => {
    useAuthStore.setState(() => ({ loading: true }));
    clearDatadogIdentity();
    disconnectSocket();
    removeAllCookies();
    useAuthStore.setState(() => ({ data: null, loading: false }));
};

export const startSignup = async (payload: ICreateAccount) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        const { data }: any = await signup(payload);
        setAuthCookies({
            token: data.token,
            userID: data._id,
        });
        setAuthCookie(ESession.MUST_ANSWER_QUESTION, true);
        useAuthStore.setState(() => ({
            data,
            mustAnswerQuestion: true,
            token: data.token,
            loading: false,
        }));
        setAuthCookie(ESession.MUST_ANSWER_QUESTION, true);
    } catch (error: any) {
        console.error('[startSignup] Error', error);
        useAuthStore.setState(() => ({ loading: false, error: error.message }));
    }
};

export const startLoadMarketingData = async (values: any) => {
    try {
        useAuthStore.setState(() => ({ loading: true }));
        await deliveryMarketingData(values);
        useAuthStore.setState(() => ({
            mustAnswerQuestion: false,
            loading: false,
        }));
        setAuthCookie(ESession.MUST_ANSWER_QUESTION, false);
    } catch (error: any) {
        console.error('[startLoadMarketingData] Error', error);
        useAuthStore.setState(() => ({ loading: false, error: error.message }));
    }
};

// 2FA Actions

export const startVerify2FA = async (code: string, isBackupCode = false) => {
    const { tempToken } = useAuthStore.getState();
    if (!tempToken) return;

    try {
        useAuthStore.setState({ loading: true, error: null });
        const payload = isBackupCode
            ? { tempToken, backupCode: code }
            : { tempToken, totpCode: code };

        const { data: user }: any = await verify2FA(payload);
        setAuthCookies({ token: user.token, userID: user._id });
        useAuthStore.setState({
            data: user,
            loading: false,
            requires2FA: false,
            tempToken: null,
        });
    } catch (error: any) {
        const message = error?.message || 'INVALID_CODE';
        useAuthStore.setState({ loading: false, error: translate2FAError(message) });
    }
};

export const startSetup2FA = async () => {
    const { tempToken } = useAuthStore.getState();

    try {
        useAuthStore.setState({ loading: true, error: null });
        const { data }: any = await setup2FA(tempToken ? { tempToken } : undefined);
        useAuthStore.setState({ setupData: data as I2FASetupData, loading: false });
    } catch (error: any) {
        const message = error?.message || 'ACTIVATION_FAILED';
        useAuthStore.setState({ loading: false, error: translate2FAError(message) });
    }
};

export const startVerifySetup2FA = async (totpCode: string) => {
    const { tempToken } = useAuthStore.getState();
    const _encrypted = useAuthStore.getState().setupData?._encrypted;
    if (!_encrypted) return;

    try {
        useAuthStore.setState({ loading: true, error: null });
        const payload: any = { totpCode, _encrypted, ...(tempToken && { tempToken }) };
        const response: any = await verifySetup2FA(payload);

        // F2 (forced): backend returns full user with token
        if (response.data?.token) {
            setAuthCookies({ token: response.data.token, userID: response.data._id });
            useAuthStore.setState({
                data: response.data,
                loading: false,
                requires2FASetup: false,
                tempToken: null,
                setupData: null,
            });
            return;
        }

        // F3 (voluntary): update user locally
        const currentUser = useAuthStore.getState().data;
        if (currentUser) {
            useAuthStore.setState({
                data: { ...currentUser, twoFactorEnabled: true } as IUser,
                loading: false,
                requires2FASetup: false,
                tempToken: null,
            });
        } else {
            useAuthStore.setState({ loading: false, requires2FASetup: false, tempToken: null });
        }
    } catch (error: any) {
        const message = error?.message || 'INVALID_CODE';
        useAuthStore.setState({ loading: false, error: translate2FAError(message) });
    }
};
